// ============================================
// Defined Terms Registry
// Master glossary of defined terms across all
// legal documents. Terms established in the TOS
// flow downstream to every dependent document.
// ============================================

import { DefinedTermsRegistry } from '../../types/docops';

export const DEFINED_TERMS_REGISTRY: DefinedTermsRegistry = {
  terms: [
    // ── Terms of Service (anchor document) ──

    {
      term: 'Service',
      definition:
        'the HemoLogix platform, website, mobile applications, and all related services',
      definedIn: '1.1-terms-of-service',
      usedIn: [
        '1.1-terms-of-service',
        '1.2-privacy-policy',
        '1.3-consumer-health-data-policy',
        '1.7-telehealth-informed-consent',
        '1.5-cookie-policy',
        '1.6-refund-return-policy',
      ],
    },
    {
      term: 'Platform Technology',
      definition:
        'all software, algorithms, AI models, user interfaces, databases, content, and related technology',
      definedIn: '1.1-terms-of-service',
      usedIn: [
        '1.1-terms-of-service',
        '3.3-ip-license-agreement',
        '2.1-operating-agreement',
      ],
    },
    {
      term: 'IP Owner',
      definition:
        'the third-party intellectual property owner who licenses Platform Technology to HemoLogix LLC',
      definedIn: '1.1-terms-of-service',
      usedIn: [
        '1.1-terms-of-service',
        '3.3-ip-license-agreement',
        '2.1-operating-agreement',
        '7.1-employment-agreement',
      ],
    },
    {
      term: 'User Content',
      definition:
        'content uploaded by users, including laboratory results, health information, and communications',
      definedIn: '1.1-terms-of-service',
      usedIn: [
        '1.1-terms-of-service',
        '1.2-privacy-policy',
      ],
    },
    {
      term: 'Feedback',
      definition:
        'feedback, suggestions, ideas, or other input regarding the Service',
      definedIn: '1.1-terms-of-service',
      usedIn: [
        '1.1-terms-of-service',
        '7.1-employment-agreement',
      ],
    },

    // ── Tier definitions (TOS) ──

    {
      term: 'Tier 1 / AI-Powered Lab Analysis',
      definition:
        'AI-powered educational comparison tool that analyzes user-uploaded lab results against published functional medicine optimal ranges. Provided at no charge to registered users.',
      definedIn: '1.1-terms-of-service',
      usedIn: [
        '1.1-terms-of-service',
        '1.2-privacy-policy',
      ],
    },
    {
      term: 'Tier 2 / Telehealth Consultations',
      definition:
        'telehealth consultation services provided through OpenLoop at $49 per consultation',
      definedIn: '1.1-terms-of-service',
      usedIn: [
        '1.1-terms-of-service',
        '1.7-telehealth-informed-consent',
        '1.2-privacy-policy',
      ],
    },
    {
      term: 'Tier 3 / Supplement Marketplace',
      definition:
        'curated marketplace of supplements with variable per-product pricing',
      definedIn: '1.1-terms-of-service',
      usedIn: [
        '1.1-terms-of-service',
        '1.2-privacy-policy',
        '1.6-refund-return-policy',
        '1.8-supplement-disclaimer',
      ],
    },

    // ── Privacy Policy ──

    {
      term: 'Health Data',
      definition:
        'personal health information including laboratory results, biomarker data, health questionnaire responses, and related health records',
      definedIn: '1.2-privacy-policy',
      usedIn: [
        '1.2-privacy-policy',
        '1.3-consumer-health-data-policy',
        '1.7-telehealth-informed-consent',
      ],
    },

    // ── Consumer Health Data Policy ──

    {
      term: 'Consumer Health Data',
      definition:
        'consumer health data as defined under the Washington My Health My Data Act (MHMDA)',
      definedIn: '1.3-consumer-health-data-policy',
      usedIn: [
        '1.3-consumer-health-data-policy',
        '1.2-privacy-policy',
        '1.1-terms-of-service',
      ],
    },

    // ── HIPAA Notice of Privacy Practices ──

    {
      term: 'Protected Health Information (PHI)',
      definition:
        'individually identifiable health information as defined under HIPAA',
      definedIn: '1.4-hipaa-npp',
      usedIn: [
        '1.4-hipaa-npp',
        '5.1-baa-llc-pc',
        '1.2-privacy-policy',
      ],
    },

    // ── Business Associate Agreement ──

    {
      term: 'Covered Entity',
      definition:
        'a healthcare provider, health plan, or healthcare clearinghouse as defined under HIPAA',
      definedIn: '5.1-baa-llc-pc',
      usedIn: [
        '5.1-baa-llc-pc',
        '1.4-hipaa-npp',
      ],
    },
    {
      term: 'Business Associate',
      definition:
        'a person or entity that performs functions involving PHI on behalf of a Covered Entity',
      definedIn: '5.1-baa-llc-pc',
      usedIn: [
        '5.1-baa-llc-pc',
        '1.2-privacy-policy',
      ],
    },

    // ── SOC 2 / Information Security ──

    {
      term: 'Trust Service Criteria',
      definition:
        'the five SOC 2 criteria: Security (CC), Availability (A), Processing Integrity (PI), Confidentiality (C), and Privacy (P)',
      definedIn: '13.1-information-security-policy',
      usedIn: [
        '13.1-information-security-policy',
        '13.10-soc2-evidence-map',
      ],
    },
    {
      term: 'Data Classification',
      definition:
        'categorization of data into PHI, PII, Business Confidential, and Public levels with corresponding handling requirements',
      definedIn: '13.4-data-classification-policy',
      usedIn: [
        '13.4-data-classification-policy',
        '13.1-information-security-policy',
        '13.5-encryption-key-management-policy',
      ],
    },
    {
      term: 'Principle of Least Privilege',
      definition:
        'access control principle that limits user access to the minimum resources required for their job function',
      definedIn: '13.2-access-control-policy',
      usedIn: [
        '13.2-access-control-policy',
        '13.1-information-security-policy',
        '13.9-acceptable-use-policy',
      ],
    },
    {
      term: 'Audit Trail',
      definition:
        'immutable chronological record of system activities including authentication, data access, modifications, and administrative actions',
      definedIn: '13.1-information-security-policy',
      usedIn: [
        '13.1-information-security-policy',
        '13.2-access-control-policy',
        '13.7-monitoring-alerting-policy',
        '8.1-data-ops-spec',
      ],
    },
  ],
};
